import { useState } from 'react';

import ContentAdd from '@mui/icons-material/Add';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useNotify, WithListContext } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Button } from 'shared/mui/Buttons';
import { DeleteDialog } from 'shared/mui/Dialogs';
import { DeleteIconButton } from 'shared/mui/IconButtons';
import { DateTimeField, TextField } from 'shared/react-admin/Fields';
import { ListPage } from 'shared/react-admin/Pages';
import { ActionsSlots } from 'shared/react-admin/Table/ActionsSlots/ActionsSlots';
import { ApiClient } from 'types/embedded/apiClient';
import { ApiKey } from 'types/embedded/apiKey';
import { Permission } from 'types/embedded/permission';

import { StyledDatagrid } from './ApiClientsShowApiKeys.styled';
import { ApiClientsShowApiKeysCreateDialog } from './ApiClientsShowApiKeysCreateDialog/ApiClientsShowApiKeysCreateDialog';
import { ApiClientsShowApiKeysField } from './ApiClientsShowApiKeysField/ApiClientsShowApiKeysField';
import { ApiClientsShowApiKeysStatusField } from './ApiClientsShowApiKeysStatusField/ApiClientsShowApiKeysStatusField';

type Props = {
  record?: ApiClient;
};

export const ApiClientsShowApiKeys: React.FC<Props> = ({ record }) => {
  const notify = useNotify();
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();
  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
  const recordModal = useRecordModal<ApiKey>();

  return (
    <ListPage
      actions={[
        hasResourcePermissions('api_keys', [Permission.Full, Permission.Create]) && (
          <Button
            key="create"
            label="Добавить"
            onClick={() => setIsOpenCreateDialog(true)}
            startIcon={<ContentAdd />}
            sx={{ marginTop: '8px', padding: '3px 16px' }}
            variant="contained"
          />
        ),
      ]}
      empty={false}
      filter={{ apiClient: record?.id }}
      headerTitle={{
        titleText: 'Список ключей API',
        titleSize: 'small',
      }}
      resource="api_keys"
      sort={{ field: 'id', order: 'ASC' }}
      sx={{ marginTop: 0, marginBottom: 2 }}
    >
      <WithListContext
        render={({ data, isLoading }) => {
          const showAlert = !data?.length && !isLoading;

          return (
            <>
              {showAlert ? (
                <Alert severity="info">
                  Здесь пока ключей API. Чтобы добавить ключи API, нажмите кнопку “Добавить”.
                </Alert>
              ) : (
                <>
                  <StyledDatagrid bulkActionButtons={false}>
                    <TextField label="ID" sortBy="id" source="originId" />
                    {hasResourceAccess('api_keys') && (
                      <ApiClientsShowApiKeysField label="Ключ API" />
                    )}
                    <ApiClientsShowApiKeysStatusField label="Состояние" />
                    <DateTimeField label="Дата создания" source="createdAt" />
                    <ActionsSlots
                      render={(record: ApiKey) => (
                        <>
                          {hasResourcePermissions('api_keys', [
                            Permission.Full,
                            Permission.Delete,
                          ]) && (
                            <DeleteIconButton
                              onClick={() => recordModal.handleOpenRecordModal(record)}
                            />
                          )}
                        </>
                      )}
                    />
                  </StyledDatagrid>
                  {recordModal.openRecordModal && (
                    <DeleteDialog
                      alert={
                        <Alert severity="warning">{`В случае удаления клиента API с признаком ${record?.active ? '"Активен"' : '"Не активен"'}, доступ к ресурсам public API для клиента ${record?.name} будет приостановлен.`}</Alert>
                      }
                      details="Восстановить значение ключа API, после его удаления будет невозможно."
                      onClose={recordModal.handleCloseRecordModal}
                      onSuccess={() => {
                        notify(`Ключ API ${recordModal.record?.value} удалён`, {
                          type: 'success',
                        });
                      }}
                      open={recordModal.openRecordModal}
                      record={recordModal.record}
                      title="Удаление ключа API"
                    />
                  )}
                </>
              )}
              {isOpenCreateDialog && (
                <ApiClientsShowApiKeysCreateDialog
                  apiClient={record}
                  onClose={() => setIsOpenCreateDialog(false)}
                  open={isOpenCreateDialog}
                />
              )}
            </>
          );
        }}
      />
    </ListPage>
  );
};
